import React from 'react';

import {
  Theme,
  makeStyles,
  Button,
  Box,
} from '@material-ui/core';

import { Add, ArrowBack } from '@material-ui/icons';
import Reserve, { TAppReserves } from '../../types/IAppReservesStore';
import NavItem from './Nav/Item';
import { useWindowSize } from '../../helpers/callHelpers';
import classNames from "classnames";

const tabWidth = 206;
const backButtonWidth = 72;
const addButtonWidth = 64;
const serviceButtonsWidth = backButtonWidth + addButtonWidth;

const useStyles = makeStyles((theme: Theme) => ({
  nav: {
    display: 'flex',
    height: 55,
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
    backgroundColor: '#ffffff',
    zIndex: 20,
    alignItems: 'center', // Выравнивание по центру для адаптивной высоты
    [theme.breakpoints.only('xs')]: {
      height: 48,
      minHeight: 48,
    },
    [theme.breakpoints.only('sm')]: {
      height: 50,
      minHeight: 50,
    },
  },
  actionButton: {
    boxShadow: 'none',
    borderRadius: 0,
  },
  buttonBorder: {
    borderRight: '1px solid #e7ebee',
  },
  backButton: {
    width: backButtonWidth,
    height: '100%', // Занимает всю высоту nav
    padding: '0 !important', // Убираем padding для соответствия высоте nav
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.only('xs')]: {
      width: '48px !important', // Соответствует высоте nav
      height: '48px !important',
    },
    [theme.breakpoints.only('sm')]: {
      width: '50px !important', // Соответствует высоте nav
      height: '50px !important',
    },
  },
  reserveButton: {
    width: tabWidth,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  reserveButtonLong: {
    justifyContent: 'flex-start',
  },
  reserveWarning: {
    backgroundColor: 'rgba(244, 88, 88, 0.2)',
  },
  reserveWarningLabel: {
    color: 'rgba(50, 50, 50, 0.5)',
  },
  addButton: {
    minWidth: 'auto !important', // Убираем минимальную ширину для квадратной кнопки
    width: addButtonWidth,
    height: '100%', // Занимает всю высоту nav
    padding: '0 !important', // Убираем padding для квадратной формы
    '& svg': {
      fontSize: 28,
    },
    [theme.breakpoints.only('xs')]: {
      width: '56px !important', // Немного меньше для мобильных
      '& svg': {
        fontSize: 24,
      },
    },
    [theme.breakpoints.only('sm')]: {
      width: '60px !important',
      '& svg': {
        fontSize: 26,
      },
    },
  },
}));

interface IProps {
  reserves: TAppReserves;
  onBack: () => void;
  onAddReserve: () => void;
  onClickReserve: (reserve: Reserve) => void;
}


const Nav: React.FunctionComponent<IProps> = ({
  reserves,
  onBack,
  onAddReserve,
  onClickReserve,
}) => {
  const {
    nav,
    actionButton,
    backButton,
    buttonBorder,
    addButton,
  } = useStyles();

  const size = useWindowSize();

  let maxWidth: number | undefined;
  let itemWidth: number | undefined;

  if (size.width) {
    // Ширина кнопки назад и кнопки добавить
    maxWidth = size.width - serviceButtonsWidth;

    // Если открыта боковая панель слева
    if (size.width > 1280) {
      maxWidth -= 270;
    }

    itemWidth = Math.min(maxWidth / reserves.length, tabWidth);
  }

  return (
    <Box clone display="flex" flexDirection="row">
      <nav className={nav}>
        <Box clone>
          <Button
            className={classNames(actionButton, backButton, buttonBorder)}
            onClick={onBack}
          >
            <ArrowBack />
          </Button>
        </Box>

        <Box
          display="flex"
          flexWrap="nowrap"
          style={{ maxWidth: maxWidth || `calc(100vw - ${serviceButtonsWidth}px)` }}
        >
          {reserves.map(
            (reserve: Reserve) => (
              <NavItem
                key={reserve.key}
                reserve={reserve}
                onClick={() => onClickReserve(reserve)}
                open={reserve.open}
                width={itemWidth}
              />
            ),
          )}
        </Box>

        <Box clone>
          <Button
            variant="contained"
            color="primary"
            className={classNames(actionButton, addButton)}
            onClick={() => onAddReserve()}
          >
            <Add />
          </Button>
        </Box>
      </nav>
    </Box>
  );
};

export default Nav;
